/**
 * ExtranetProfile — dados do usuário externo + alteração de senha.
 */
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useExtranetAuth } from '@/hooks/useExtranetAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export default function ExtranetProfile() {
  const { externalUser, tenant } = useExtranetAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Senha deve ter ao menos 8 caracteres');
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPassword('');
    toast.success('Senha atualizada com sucesso');
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <Helmet>
        <title>Meu perfil</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Meu perfil</h1>
        <p className="text-sm text-muted-foreground">
          Seus dados de acesso ao portal de {tenant?.name}.
        </p>
      </div>

      <Card className="p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">Nome</div>
            <div className="font-medium">{externalUser?.display_name || '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">E-mail</div>
            <div className="font-medium break-all">{externalUser?.email || '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Empresa</div>
            <div className="font-medium">{externalUser?.organization || '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Status</div>
            <div className="font-medium">
              {externalUser?.is_active ? 'Ativo' : 'Aguardando aprovação'}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="font-medium mb-3">Alterar senha</h2>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new_password">Nova senha</Label>
            <Input
              id="new_password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              maxLength={200}
              autoComplete="new-password"
              required
            />
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Atualizar senha
          </Button>
        </form>
      </Card>
    </div>
  );
}
